import './MetricsSection.scss';

import { CrudFilter, stringifyTableParams, useList } from '@refinedev/core';
import { Flex } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import MetricCard, { MetricCardTone } from '@/components/general/MetricCard';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { useUserContext } from '@/contexts/UserContext';
import User from '@/model/General/User';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { RECEPTION_TYPE } from '@/model/Logistic/Reception/ReceptionType';
import { SERVICE_ORDER_STATUS } from '@/model/ServiceOrder/ServiceOrder';
import { TICKET_LOGISTIC_STATE, TICKET_STATE } from '@/model/Ticket/TicketConstants';
import { RESOURCES } from '@/Resources';

const METRIC_QUERY_PAGE_SIZE = 1;

interface HomeMetricDefinition {
  key: string;
  title: string;
  description?: string;
  linkLabel?: string;
  linkFilter?: CrudFilter[];
  group?: string;
  tone?: MetricCardTone;
  resource: RESOURCES;
  localResourceLink?: RESOURCES;
  filters: CrudFilter[];
  refetchIntervalMs?: number;
}

interface HomeListMetricCardProps {
  metric: HomeMetricDefinition;
}

interface HomeMetricGroup {
  key: string;
  title: string;
  metrics: HomeMetricDefinition[];
}

const GENERAL_METRIC_GROUP_KEY = 'general';
const GENERAL_METRIC_GROUP_TITLE = 'General';
/**
 * @metadata HomeMetricStatusFilter
 * Mantiene consistente el filtro de estados entre métricas y listados.
 */
const buildStatusFilter = (statuses: any[]): CrudFilter[] => [
  {
    field: 'status',
    operator: 'eq',
    value: statuses.join(','),
  },
];
/**
 * @metadata HomeMetricListPath
 * Construye el link al listado conservando los filtros aplicados en la métrica.
 */
const buildListPathWithFilters = (
  resource: RESOURCES,
  filters: CrudFilter[],
): string | undefined => {
  const basePath = resource;

  if (!basePath) {
    return undefined;
  }

  const queryString = stringifyTableParams({
    sorters: [],
    filters,
  });

  return queryString ? `${basePath}?${queryString}` : basePath;
};

/**
 * @metadata HomeMetricGroups
 * Agrupa métricas por nombre y ubica "General" primero para métricas sin grupo explícito.
 */
const groupMetrics = (metrics: HomeMetricDefinition[]): HomeMetricGroup[] => {
  const generalMetrics: HomeMetricDefinition[] = [];
  const groupMap = new Map<string, HomeMetricGroup>();

  metrics.forEach(metric => {
    const groupName = metric.group?.trim();

    if (!groupName) {
      generalMetrics.push(metric);

      return;
    }

    if (!groupMap.has(groupName)) {
      groupMap.set(groupName, {
        key: groupName,
        title: groupName,
        metrics: [],
      });
    }

    groupMap.get(groupName)?.metrics.push(metric);
  });

  return [
    ...(generalMetrics.length > 0
      ? [
          {
            key: GENERAL_METRIC_GROUP_KEY,
            title: GENERAL_METRIC_GROUP_TITLE,
            metrics: generalMetrics,
          },
        ]
      : []),
    ...Array.from(groupMap.values()),
  ];
};

const HomeListMetricCard = ({ metric }: HomeListMetricCardProps) => {
  const { t } = useTranslation();
  const {
    result: { total },
    query: { isLoading, isError },
  } = useList({
    resource: metric.resource,
    pagination: {
      currentPage: 1,
      pageSize: METRIC_QUERY_PAGE_SIZE,
      mode: 'server',
    },
    filters: metric.filters,
    queryOptions: {
      refetchInterval: metric.refetchIntervalMs ?? 300000,
      retry: false,
    },
  });

  return (
    <MetricCard
      title={t(metric.title)}
      value={isError ? '-' : (total ?? 0)}
      description={metric.description ? t(metric.description) : undefined}
      linkTo={buildListPathWithFilters(
        metric.localResourceLink ? metric.localResourceLink : metric.resource,
        metric.linkFilter ? metric.linkFilter : metric.filters,
      )}
      linkLabel={metric.linkLabel ? t(metric.linkLabel) : undefined}
      loading={isLoading}
      tone={metric.tone}
    />
  );
};

/**
 * @metadata HomeMetricsByWorkArea
 * Define las métricas visibles por área para poder extenderlas sin tocar el render.
 */
const getHomeMetricDefinitions = (
  userWorkArea?: USER_WORK_AREA,
  user?: User,
): HomeMetricDefinition[] => {
  if (userWorkArea === USER_WORK_AREA.LOGISTICS) {
    return [
      {
        key: 'pending-shipments',
        title: 'Envíos pendientes',
        description: 'Envíos con estado pendiente de gestión.',
        linkLabel: 'Ver listado de envíos',
        tone: 'warning',
        resource: RESOURCES.SHIPMENTS,
        filters: buildStatusFilter([PACKAGE_STATES.PENDIENTE]),
        group: 'Gestión de envíos',
      },
      {
        key: 'traveling-shipments',
        title: 'Envíos en viaje',
        description: 'Envíos que están en proceso de entrega.',
        linkLabel: 'Ver listado de envíos',
        tone: 'info',
        resource: RESOURCES.SHIPMENTS,
        filters: buildStatusFilter([PACKAGE_STATES.EN_VIAJE]),
        group: 'Gestión de envíos',
      },
      {
        key: 'pending-receptions',
        title: 'Recepciones pendientes',
        description: 'Recepciones pendientes de ingreso.',
        linkLabel: 'Ver listado de recepciones',
        tone: 'info',
        resource: RESOURCES.RECEPTIONS,
        filters: buildStatusFilter([PACKAGE_STATES.PENDIENTE]),
        group: 'Gestión de recepciones',
      },
      {
        key: 'repair-request',
        title: 'Solicitudes de reparación',
        description: 'Solicitudes de reparación pendientes de gestión.',
        linkLabel: 'Ver listado de solicitudes de reparación',
        tone: 'info',
        resource: RESOURCES.RECEPTIONS,
        filters: [
          ...buildStatusFilter([PACKAGE_STATES.PENDIENTE, PACKAGE_STATES.EN_VIAJE]),
          { field: 'tipo', operator: 'eq', value: RECEPTION_TYPE.SOLICITUD_REPARACION_REVISION },
        ],
        group: 'Gestión de recepciones',
      },
      {
        key: 'processing-cases',
        title: 'Casos pendientes de resolución',
        description: 'Casos que no han finalizado su gestión logística.',
        linkLabel: 'Ver listado de casos',
        tone: 'info',
        resource: RESOURCES.TICKETS,
        localResourceLink: RESOURCES.CASE_RESOLUTION,
        filters: [
          {
            field: 'logistics_state',
            operator: 'eq',
            value: TICKET_LOGISTIC_STATE.EN_CURSO,
          },
          { field: 'has_operations_associated', operator: 'eq', value: true },
          { field: 'include_logistics_vars', operator: 'eq', value: true },
        ],
        group: 'Gestión de casos',
      },
      {
        key: 'ready-to-resolve-cases',
        title: 'Casos listos para resolver',
        description:
          'Casos que han finalizado su gestión logística y están listos para asignar una resolución.',
        linkLabel: 'Ver listado de casos',
        tone: 'warning',
        resource: RESOURCES.TICKETS,
        localResourceLink: RESOURCES.CASE_RESOLUTION,
        filters: [
          {
            field: 'logistics_state',
            operator: 'eq',
            value: TICKET_LOGISTIC_STATE.LISTO_PARA_RESOLVER,
          },
          { field: 'has_operations_associated', operator: 'eq', value: true },
          { field: 'include_logistics_vars', operator: 'eq', value: true },
        ],
        group: 'Gestión de casos',
      },
    ];
  } else if (userWorkArea === USER_WORK_AREA.CUSTOMER_SERVICE) {
    return [
      {
        key: 'open-tickets',
        title: 'Tickets abiertos',
        description: 'Tickets que aun no fueron resueltos.',
        tone: 'info',
        resource: RESOURCES.TICKETS,
        filters: buildStatusFilter([TICKET_STATE.OPEN]),
        linkFilter: [
          ...buildStatusFilter([TICKET_STATE.OPEN]),
          { field: 'assigned_to_str', operator: 'eq', value: user?.username },
        ],
        linkLabel: 'Ver mis tickets abiertos',
        group: 'Gestión de tickets',
      },
      {
        key: 'non-tracking-tickets',
        title: 'Tickets sin seguimiento',
        description: 'Tickets abiertos que no tienen un seguimiento registrado.',
        tone: 'warning',
        resource: RESOURCES.TICKETS,
        filters: [
          ...buildStatusFilter([TICKET_STATE.OPEN]),
          { field: 'no_tracking', operator: 'eq', value: true },
        ],
        linkFilter: [
          ...buildStatusFilter([TICKET_STATE.OPEN]),
          { field: 'no_tracking', operator: 'eq', value: true },
          { field: 'assigned_to_str', operator: 'eq', value: user?.username },
        ],
        linkLabel: 'Ver tickets sin seguimiento',
        group: 'Gestión de tickets',
      },
      {
        key: 'coordinated-services',
        title: 'Servicio coordinados',
        description: 'Servicios al cliente coordinados con los técnicos.',
        tone: 'info',
        resource: RESOURCES.SERVICE_ORDERS,
        filters: [...buildStatusFilter([SERVICE_ORDER_STATUS.COORDINADA])],
        linkLabel: 'Ver servicios coordinados',
        group: 'Gestión de servicios',
      },
      {
        key: 'services-pending-coordination',
        title: 'Servicios pendientes de coordinación',
        description: 'Servicios al cliente que aún no han sido coordinados con los técnicos.',
        tone: 'warning',
        resource: RESOURCES.SERVICE_ORDERS,
        filters: [...buildStatusFilter([SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION])],
        linkLabel: 'Ver servicios pendientes de coordinación',
        group: 'Gestión de servicios',
      },
    ];
  } else if (userWorkArea === USER_WORK_AREA.ADMINISTRATION) {
    return [
      {
        key: 'pending-billing-service',
        title: 'Servicios pendientes de facturación',
        description: 'Servicios que aún no han sido facturados.',
        tone: 'warning',
        resource: RESOURCES.SERVICE_ORDERS,
        filters: buildStatusFilter([SERVICE_ORDER_STATUS.PENDIENTE_DE_FACTURACION]),
        linkLabel: 'Ver servicios pendientes de facturación',
        group: 'Gestión administrativa',
      },
      {
        key: 'pending-spare-parts-collections',
        title: 'Ventas de repuestos pendientes',
        description: 'Ventas de repuestos pendientes de facturación.',
        tone: 'warning',
        resource: RESOURCES.SHIPMENTS,
        localResourceLink: RESOURCES.SPARE_PARTS_COLLECTIONS,
        filters: [...buildStatusFilter([PACKAGE_STATES.GESTION_PAGO])],
        linkLabel: 'Ver ventas de repuestos pendientes',
        group: 'Gestión administrativa',
      },
    ];
  }

  return [];
};

/**
 * @metadata MetricsSection
 * Sección de métricas agrupadas para el home según área y permisos del usuario.
 */
const MetricsSection = () => {
  const { t } = useTranslation();
  const { canAny } = usePermissionContext();
  const { userLogged } = useUserContext();
  // const [workAreaMetrics, setWorkAreaMetrics] = useState<USER_WORK_AREA | undefined>(userWorkArea);
  // TODO De momento
  const visibleMetrics = useMemo(
    () =>
      [
        ...getHomeMetricDefinitions(USER_WORK_AREA.CUSTOMER_SERVICE, userLogged),
        ...getHomeMetricDefinitions(USER_WORK_AREA.LOGISTICS, userLogged),
        ...getHomeMetricDefinitions(USER_WORK_AREA.ADMINISTRATION, userLogged),
      ].filter(metric => canAny(metric.resource)),
    [canAny, userLogged],
  );
  const metricGroups = useMemo(() => groupMetrics(visibleMetrics), [visibleMetrics]);

  // useEffect(() => setWorkAreaMetrics(userWorkArea), [userWorkArea]);

  return (
    <section className="MetricsSection">
      <Flex align="center" justify="space-between">
        <h2 className="HomeSectionTitle">{t('Métricas')}</h2>
        {/* <AntdSelect
          value={workAreaMetrics}
          style={{ width: 160 }}
          options={Object.keys(UserWorkAreaString).map(key => ({
            label: t(UserWorkAreaString[key]),
            value: key,
          }))}
          onChange={v => {
            setWorkAreaMetrics(v);
          }}
        /> */}
      </Flex>
      <div className="HomeMetricsGroups">
        {metricGroups.map(group => (
          <section key={group.key} className="HomeMetricsGroup">
            <h3 className="HomeMetricsGroupTitle">{t(group.title)}</h3>
            <div className="HomeMetricsGrid">
              {group.metrics.map(metric => (
                <HomeListMetricCard key={metric.key} metric={metric} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </section>
  );
};

export default MetricsSection;
